import { App, Plugin } from "@pimo/pimo-app-builder";
import { DialogLayout, Log } from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import dayjs from "dayjs";
import debounce from "lodash.debounce";
import { Configuration } from "product-test-types";
import { canUserPerformOperation } from "product-test-utils";
import { generatePath } from "react-router-dom";

import { TestAppState } from "../app";
import { LogsWithSearch } from "../components/logs-components/logs-with-search";
import { APP_ROUTES } from "../constants";
import { STRAPI_URL } from "../env";
import { fetchEntityConfiguration } from "../helpers/fetch-entity-configurations";
import { fetchOverlay } from "../helpers/fetch-overlay";
import { fetchAllProjects } from "../helpers/fetch-project";
import { getItemNameFromState } from "../utils/get-plural-of-item-name";

const PERMISSION_INPUT_TIMEOUT_MS = 5000;
const PERMISSION_INPUT_POLL_INTERVAL_MS = 50;

const isForbiddenError = (error: unknown) =>
  error instanceof Error && error.message.includes("status 403");

export class LogsPlugin implements Plugin<TestAppState> {
  private app?: App<TestAppState>;
  private configurationPromise?: Promise<Configuration | undefined>;

  onRegister(app: App<TestAppState>): void {
    this.app = app;

    const logsView = app.createView({
      name: "logs",
      layout: new DialogLayout(),
    });

    const logsComponent = logsView.addComponent({
      component: LogsWithSearch,
    });

    logsView.mapLayoutState((state) => ({
      title:
        getLocalizedMessage(state.language || "en", "logs.title") || "Logs",
      icon: {
        alt: "pimo",
        small: "pimo-logo.svg",
        big: "pimo-logo.svg",
      },
      entries: [],
      nodes: [],
    }));

    logsView.on("dialog:close", async () => {
      if (await this.canUserViewLogs()) {
        app.navigate(APP_ROUTES.dashboard);
      } else {
        const projects = await fetchAllProjects();

        app.navigate(
          generatePath(APP_ROUTES.report, {
            id: projects?.[0].id ?? "",
            viewname: "dashboard",
          })
        );
      }
    });

    logsView.on("dialog:navigate", (payload) => {
      if (!payload.payload) return;
      app.navigate(payload.payload.currentPath);
    });

    logsComponent.mapState((state) => {
      const mappedProjects =
        state.overlay?.projects?.map((m) => ({
          id: m.id ?? "",
          name: m.name ?? "",
        })) ?? [];

      const uniqueEditors = (state.uniqueEditors ?? []).map(
        (editor: string) => ({
          id: editor,
          name: editor,
        })
      );

      return {
        logs: state.logs ?? [],
        isLoading: state.isLoading ?? false,
        fromDate: state.logsSearch?.fromDate ?? null,
        untilDate: state.logsSearch?.untilDate ?? null,
        selectedProject: state.logsSearch?.selectedProject ?? "",
        selectedEditor: state.logsSearch?.selectedEditor ?? "",
        searchTerm: state.logsSearch?.searchTerm ?? "",
        projects: mappedProjects,
        editors: uniqueEditors,
        language: state.language || "en",
        itemName: getItemNameFromState(state),
      };
    });

    const performSearch = async () => {
      if (!this.app) return;

      this.app.patchAppState({ isLoading: true });

      if (!(await this.canUserViewLogs())) {
        this.app.patchAppState({ logs: [], isLoading: false });
        return;
      }

      const state = this.app.getAppState();
      const searchParams = state.logsSearch;

      try {
        const searchOptions = {
          offset: 0,
          limit: 50,
          search: searchParams?.searchTerm || undefined,
          editor: searchParams?.selectedEditor || undefined,
          projectId: searchParams?.selectedProject || undefined,
          fromDate: searchParams?.fromDate
            ? dayjs(searchParams.fromDate).format("YYYY-MM-DD")
            : undefined,
          untilDate: searchParams?.untilDate
            ? dayjs(searchParams.untilDate).format("YYYY-MM-DD")
            : undefined,
        };

        const logs = await this.fetchLogs(searchOptions);

        this.app.patchAppState({
          logs,
          isLoading: false,
        });
      } catch (error) {
        console.error("Error performing search:", error);

        this.app.patchAppState({
          isLoading: false,
          ...(isForbiddenError(error)
            ? {}
            : {
                snackbar: {
                  open: true,
                  message:
                    getLocalizedMessage(
                      state.language || "en",
                      "logs.searchFailed"
                    ) || "Failed to search logs",
                  severity: "error",
                  type: "snackbar",
                },
              }),
        });
      }
    };

    const debouncedPerformSearch = debounce(performSearch, 300);

    logsComponent.on("search:from-date-changed", ({ payload }) => {
      app.patchAppState({
        logsSearch: {
          ...app.getAppState().logsSearch,
          fromDate: payload?.fromDate,
        },
      });
      performSearch();
    });

    logsComponent.on("search:until-date-changed", ({ payload }) => {
      app.patchAppState({
        logsSearch: {
          ...app.getAppState().logsSearch,
          untilDate: payload?.untilDate,
        },
      });
      performSearch();
    });

    logsComponent.on("search:project-changed", ({ payload }) => {
      app.patchAppState({
        logsSearch: {
          ...app.getAppState().logsSearch,
          selectedProject: payload?.selectedProject ?? "",
        },
      });
      performSearch();
    });

    logsComponent.on("search:editor-changed", ({ payload }) => {
      app.patchAppState({
        logsSearch: {
          ...app.getAppState().logsSearch,
          selectedEditor: payload?.selectedEditor,
        },
      });
      performSearch();
    });

    logsComponent.on("search:term-changed", ({ payload }) => {
      app.patchAppState({
        logsSearch: {
          ...app.getAppState().logsSearch,
          searchTerm: payload?.searchTerm,
        },
      });

      debouncedPerformSearch();
    });

    logsComponent.on("search:clear-all", () => {
      app.patchAppState({
        logsSearch: {
          fromDate: null,
          untilDate: null,
          selectedProject: "",
          selectedEditor: "",
          searchTerm: "",
        },
      });
      performSearch();
    });

    logsComponent.on("search:clear-search-term", () => {
      app.patchAppState({
        logsSearch: {
          ...app.getAppState().logsSearch,
          searchTerm: "",
        },
      });
      performSearch();
    });

    logsComponent.on("overlay:load-more-logs", async () => {
      const { logs, isLoading, logsSearch } = app.getAppState();

      if (isLoading || !logs || logs.length === 0) return;
      if (!(await this.canUserViewLogs())) return;

      app.patchAppState({ isLoading: true });

      const offset = logs.length;

      try {
        const searchOptions = {
          offset,
          limit: 20,
          search: logsSearch?.searchTerm || undefined,
          editor: logsSearch?.selectedEditor || undefined,
          projectId: logsSearch?.selectedProject || undefined,
          fromDate: logsSearch?.fromDate
            ? dayjs(logsSearch.fromDate).format("YYYY-MM-DD")
            : undefined,
          untilDate: logsSearch?.untilDate
            ? dayjs(logsSearch.untilDate).format("YYYY-MM-DD")
            : undefined,
        };

        const newLogs = await this.fetchLogs(searchOptions);

        if (newLogs.length > 0) {
          const existingLogIds = new Set(
            logs.map(
              (log: Log) =>
                `${log.createdAt}-${log.updatedFieldName}-${log.initiator}`
            )
          );
          const uniqueNewLogs = newLogs.filter(
            (log: Log) =>
              !existingLogIds.has(
                `${log.createdAt}-${log.updatedFieldName}-${log.initiator}`
              )
          );

          if (uniqueNewLogs.length > 0) {
            app.patchAppState({
              logs: [...logs, ...uniqueNewLogs],
              isLoading: false,
            });
          } else {
            app.patchAppState({ isLoading: false });
          }
        } else {
          app.patchAppState({ isLoading: false });
        }
      } catch (error) {
        console.error("Error fetching more logs:", error);

        app.patchAppState({
          isLoading: false,
          ...(isForbiddenError(error)
            ? {}
            : {
                snackbar: {
                  open: true,
                  message:
                    getLocalizedMessage(
                      this.app?.getAppState().language || "en",
                      "logs.loadMoreFailed"
                    ) || "Failed to load more logs",
                  severity: "error",
                  type: "snackbar",
                },
              }),
        });
      }
    });

    this.loadInitialLogs();
    this.loadUniqueEditors();

    const logsRoute = app.createRoute({
      path: APP_ROUTES.logs,
      view: logsView,
    });

    logsRoute.on("load", async () => {
      this.loadInitialLogs();
      this.loadUniqueEditors();
      const overlay = await fetchOverlay();
      app.patchAppState({
        overlay,
      });
    });
  }

  private async loadUniqueEditors() {
    if (!this.app) return;

    if (!(await this.canUserViewLogs())) {
      this.app.patchAppState({ uniqueEditors: [] });
      return;
    }

    try {
      const response = await fetch(`${STRAPI_URL}/api/logs/unique-editors`, {
        credentials: "include",
      });

      if (response.status >= 400) {
        throw new Error(
          `Fetch unique editors failed with status ${response.status}.`
        );
      }

      const uniqueEditors = await response.json();
      this.app.patchAppState({
        uniqueEditors,
      });
    } catch (error) {
      console.error("Error fetching unique editors:", error);
      this.app.patchAppState({
        uniqueEditors: [],
      });
    }
  }

  private async loadInitialLogs() {
    if (!this.app) return;

    this.app.patchAppState({ isLoading: true });

    if (!(await this.canUserViewLogs())) {
      this.app.patchAppState({ logs: [], isLoading: false });
      return;
    }

    const state = this.app.getAppState();
    try {
      const searchParams = state.logsSearch;

      const searchOptions = {
        projectId: searchParams?.selectedProject || undefined,
        offset: 0,
        limit: 20,
        search: searchParams?.searchTerm || undefined,
        editor: searchParams?.selectedEditor || undefined,
        fromDate: searchParams?.fromDate ?? undefined,
        untilDate: searchParams?.untilDate ?? undefined,
      };

      const logs = await this.fetchLogs(searchOptions);

      this.app.patchAppState({
        logs,
        isLoading: false,
      });
    } catch (error) {
      console.error("Error fetching logs:", error);

      this.app.patchAppState({
        logs: [],
        isLoading: false,
        ...(isForbiddenError(error)
          ? {}
          : {
              snackbar: {
                open: true,
                message:
                  getLocalizedMessage(
                    state.language || "en",
                    "logs.loadFailed"
                  ) || "Failed to load logs",
                severity: "error",
                type: "snackbar",
              },
            }),
      });
    }
  }

  /**
   * Logs are gated server-side in LogsController, which requires at least
   * program-level read access. Mirroring that check here keeps users without
   * access from firing requests that can only ever come back as 403.
   *
   * The check can run before the login plugin has populated `userProfile` and
   * before the configuration has been loaded. Deciding on missing inputs would
   * deny everyone, so we wait for them and fall back to letting the request
   * through when they never arrive - the server stays the source of truth.
   */
  private async canUserViewLogs(): Promise<boolean> {
    const userProfile = await this.waitForUserProfile();
    const configuration = await this.resolveConfiguration();

    // If the login plugin confirmed the user is not authenticated, or neither
    // input ever arrived within the timeout, deny rather than leak requests.
    if (!userProfile || !configuration) {
      return false;
    }

    return canUserPerformOperation({
      userGroups: userProfile.groups || [],
      operation: "read",
      lowestLevelAccessRequiredToPerformThisOperation: "program",
      client: configuration.client,
      tool: configuration.tool,
    });
  }

  private async waitForUserProfile(): Promise<
    TestAppState["userProfile"] | undefined
  > {
    const deadline = Date.now() + PERMISSION_INPUT_TIMEOUT_MS;

    // The login plugin starts with `isLoggedIn: false` as the initial state and
    // only sets it to `true` once the session check resolves, so we cannot use
    // `isLoggedIn === false` as a "not logged in" signal here — that is also the
    // value before the check has completed. Just wait for the profile to land.
    while (Date.now() < deadline) {
      const userProfile = this.app?.getAppState().userProfile;

      if (userProfile) return userProfile;

      await new Promise((resolve) =>
        setTimeout(resolve, PERMISSION_INPUT_POLL_INTERVAL_MS)
      );
    }

    return this.app?.getAppState().userProfile;
  }

  private async resolveConfiguration(): Promise<Configuration | undefined> {
    const stateConfiguration = this.app?.getAppState().settings?.configuration;

    if (stateConfiguration) return stateConfiguration;

    // Only fetch once per plugin instance; clear on failure so the next call
    // retries rather than re-using a rejected/undefined-resolved promise.
    if (!this.configurationPromise) {
      this.configurationPromise = fetchEntityConfiguration().catch(() => {
        this.configurationPromise = undefined;
        return undefined;
      });
    }

    return this.configurationPromise;
  }

  private async fetchLogs(params: {
    projectId?: string;
    offset: number;
    limit: number;
    search?: string;
    editor?: string;
    fromDate?: string;
    untilDate?: string;
  }) {
    const baseUrl = `${STRAPI_URL}/api/logs`;
    const url = new URL(baseUrl);

    const searchParams = new URLSearchParams({
      offset: params.offset.toString(),
      limit: params.limit.toString(),
    });

    if (params.projectId) {
      searchParams.set("projectId", params.projectId.toString());
    }

    if (params.search && params.search.trim() && params.search !== "") {
      searchParams.set("search", params.search.trim());
    }

    if (params.editor && params.editor.trim() && params.editor !== "") {
      searchParams.set("editor", params.editor.trim());
    }

    if (params.fromDate && params.fromDate.trim()) {
      searchParams.set("fromDate", params.fromDate);
    }

    if (params.untilDate && params.untilDate.trim()) {
      searchParams.set("untilDate", params.untilDate);
    }

    url.search = searchParams.toString();

    const response = await fetch(url.toString(), {
      credentials: "include",
    });

    if (response.status >= 400) {
      throw new Error(`Fetch logs failed with status ${response.status}.`);
    }

    const result = await response.json();
    return result;
  }
}
